'use client';

import React, { Suspense, useState, use } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { makeRequest } from '@/utils/axiosInterceptor';
import getUserData from '@/app/users/getUserData';
import { toast } from 'sonner';
import { Loader2, Upload, Cpu, ChevronsUpDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

type Period = 'today' | 'this_week' | 'this_month' | 'custom';

interface User { id: number; name: string; username: string; email: string; }

interface DashboardData {
  study_triage:  { uploaded: number; processed: number };
  api_instances: { uploaded: number; processed: number };
  timestamp: string;
}




function getDateRange(p: Period, customStart?: string, customEnd?: string) {
  const today = new Date();

  // ✅ Use local date parts, NOT toISOString() which converts to UTC
  const fmt = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  switch (p) {
    case 'today': {
      const s = fmt(today);
      return { start: s, end: s };
    }

    case 'this_week': {
      // Go back to last Monday, then forward 6 days to last Sunday
      const dow = today.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
      // days since last Monday: if today is Mon(1) → 7, Tue(2) → 8, ..., Sun(0) → 6
      const daysToLastMon = dow === 0 ? 6 : dow + 6;
      const lastMonday = new Date(today);
      lastMonday.setDate(today.getDate() - daysToLastMon);
      const lastSunday = new Date(lastMonday);
      lastSunday.setDate(lastMonday.getDate() + 6);
      return { start: fmt(lastMonday), end: fmt(lastSunday) };
    }

    case 'this_month': {
      // Feb 1 → Feb 28/29 (previous calendar month)
      const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastDay  = new Date(today.getFullYear(), today.getMonth(), 0);
      return { start: fmt(firstDay), end: fmt(lastDay) };
    }

    case 'custom':
      return { start: customStart!, end: customEnd! };
  }
}



function formatDateRange(p: Period, customStart?: string, customEnd?: string): string {
  if (p === 'custom') {
    if (!customStart || !customEnd) return '';
    return `${fmtDisplay(customStart)} – ${fmtDisplay(customEnd)}`;
  }
  const { start, end } = getDateRange(p);
  return start === end ? fmtDisplay(start) : `${fmtDisplay(start)} – ${fmtDisplay(end)}`;
}

function fmtDisplay(iso: string): string {
  // iso is YYYY-MM-DD
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}



// ─── Fetch helpers ────────────────────────────────────────────────────────────
async function fetchDashboard(
  p: Period,
  uid: string,
  customStart?: string,
  customEnd?: string,
): Promise<DashboardData | null> {

  const { start, end } = getDateRange(p, customStart, customEnd);

  let url = `/monitoring/dashboard-summary?startDate=${start}&endDate=${end}`;
  if (uid && uid !== 'all') url += `&userId=${encodeURIComponent(uid)}`;

  let res: any;

  try {
    res = await makeRequest('GET', url, '');
  } catch (err: any) {
      const message =
        err?.response?.data?.message ??
        err?.message ??
        'Network error';

      toast.error(message);
      return null;
    }

    const body = res?.data ?? res;

    if (body?.statusCode && body?.message) {
      toast.error(`${body.message} (${body.statusCode})`);
      return null;
    }

    if (!body?.study_triage) {
      toast.error('Unexpected server response');
      return null;
    }

    return body as DashboardData;
  }

async function fetchUsers(): Promise<User[]> {
  const u = await getUserData();
  return Array.isArray(u) ? u : [];
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function Monitoring() {
  const [period, setPeriod]           = useState<Period>('today');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd]     = useState('');
  const [isCustomApplied, setIsCustomApplied] = useState(false);
  const [userId, setUserId]           = useState('all');
  const [userLabel, setUserLabel]     = useState('All Users');

 const [dataPromise, setDataPromise] = useState<Promise<DashboardData | null>>(
  () => fetchDashboard('today', 'all')
);
  const [usersPromise, setUsersPromise] = useState<Promise<User[]> | null>(null);

  const refetch = (p: Period, uid: string, start?: string, end?: string) =>
    setDataPromise(fetchDashboard(p, uid, start, end));

  const handlePeriodClick = (p: Period) => {
    setPeriod(p);
    if (p !== 'custom') { setIsCustomApplied(false); refetch(p, userId); }
  };

  const handleApplyCustom = () => {
    if (!customStart || !customEnd) { toast.error('Select both dates'); return; }
    if (new Date(customStart) > new Date(customEnd)) { toast.error('Start must be before end'); return; }
    setIsCustomApplied(true);
    refetch('custom', userId, customStart, customEnd);
  };

  const handleUserSelect = (uid: string, label: string) => {
    setUserId(uid);
    setUserLabel(label);
    if (period === 'custom') {
      if (isCustomApplied && customStart && customEnd) refetch('custom', uid, customStart, customEnd);
      return;
    }
    refetch(period, uid);
  };

  return (
    <div className="h-full overflow-y-auto p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="text-2xl font-bold">Uploads</h3>
          <p className="text-muted-foreground mt-1">Track study uploads and AI processing metrics</p>
        </div>

        <UserCombobox
          selectedId={userId}
          selectedLabel={userLabel}
          usersPromise={usersPromise}
          onOpen={() => { if (!usersPromise) setUsersPromise(fetchUsers()); }}
          onSelect={handleUserSelect}
        />
      </div>

      <div className="flex gap-3 flex-wrap">
        {(['today', 'this_week', 'this_month', 'custom'] as Period[]).map((p) => (
          <Button key={p} variant={period === p ? 'default' : 'outline'} onClick={() => handlePeriodClick(p)}>
            {{ today: 'Today', this_week: 'Last Week', this_month: 'Last Month', custom: 'Custom' }[p]}
          </Button>
        ))}
      </div>

      {(period !== 'custom' || isCustomApplied) && (
        <p className="text-sm text-muted-foreground">
          {formatDateRange(period, customStart, customEnd)}
        </p>
      )}

      {period === 'custom' && (
        <Card>
          <CardHeader><CardTitle className="text-base">Custom Date Range</CardTitle></CardHeader>
          <CardContent>
            <div className="flex gap-4 items-end flex-wrap">
              <div>
                <label className="text-sm font-medium block mb-2">Start Date</label>
                <Input type="date" value={customStart} onChange={(e) => { setCustomStart(e.target.value); setIsCustomApplied(false); }} />
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">End Date</label>
                <Input type="date" value={customEnd} onChange={(e) => { setCustomEnd(e.target.value); setIsCustomApplied(false); }} />
              </div>
              <Button onClick={handleApplyCustom}>Apply</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {(period !== 'custom' || isCustomApplied) && (
        <Suspense fallback={<MetricsSkeleton />}>
          <MetricsCards promise={dataPromise} />
        </Suspense>
      )}
    </div>
  );
}

// ─── UserCombobox ─────────────────────────────────────────────────────────────

function UserCombobox({ selectedId, selectedLabel, usersPromise, onOpen, onSelect }: {
  selectedId: string;
  selectedLabel: string;
  usersPromise: Promise<User[]> | null;
  onOpen: () => void;
  onSelect: (id: string, label: string) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) onOpen(); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-[220px] justify-between font-normal">
          <span className="truncate">{selectedLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-0" align="end">
        <Command>
          <CommandInput placeholder="Search users..." />
          <CommandList>
            <CommandEmpty>No users found.</CommandEmpty>
            <CommandGroup>
              <CommandItem value="all" onSelect={() => { onSelect('all', 'All Users'); setOpen(false); }}>
                <Check className={cn('mr-2 h-4 w-4', selectedId === 'all' ? 'opacity-100' : 'opacity-0')} />
                All Users
              </CommandItem>
              {usersPromise && (
                <Suspense fallback={
                  <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" /> Loading...
                  </div>
                }>
                  <UserItems promise={usersPromise} selectedId={selectedId} onSelect={onSelect} onClose={() => setOpen(false)} />
                </Suspense>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ─── UserItems — suspends until users resolve, then renders searchable items ──

function UserItems({ promise, selectedId, onSelect, onClose }: {
  promise: Promise<User[]>;
  selectedId: string;
  onSelect: (id: string, label: string) => void;
  onClose: () => void;
}) {
  const users = use(promise);
  return (
    <>
      {users.map((u) => {
        const id    = String(u.id);
        const label = u.name ?? u.username ?? u.email;
        return (
          <CommandItem
            key={id}
            // include username + email in value so CommandInput searches all three
            value={`${label} ${u.username} ${u.email}`}
            onSelect={() => { onSelect(id, label); onClose(); }}
          >
            <Check className={cn('mr-2 h-4 w-4', selectedId === id ? 'opacity-100' : 'opacity-0')} />
            <div className="flex flex-col">
              <span className="text-sm">{label}</span>
              <span className="text-xs text-muted-foreground">@{u.username}</span>
            </div>
          </CommandItem>
        );
      })}
    </>
  );
}

// ─── MetricsCards ─────────────────────────────────────────────────────────────

function MetricsCards({ promise }: { promise: Promise<DashboardData | null> }) {
  const data = use(promise);

  if (!data) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground">
          No data available
        </CardContent>
      </Card>
    );
  }
  const stat = data.study_triage;
  const api  = data.api_instances;

  return (
    <TooltipProvider>
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <MetricCard title="Studies Uploaded"  value={stat.uploaded}  subtitle="User uploaded studies"    icon={<Upload className="h-4 w-4 text-muted-foreground" />} tooltip="Total studies uploaded by users" />
          <MetricCard title="Processed by AI"   value={stat.processed} subtitle="Total processed by AI"    icon={<Cpu    className="h-4 w-4 text-muted-foreground" />} tooltip="Total studies analysed by AI" />
          {api.uploaded  > 0 && <MetricCard title="API Study Uploaded" value={api.uploaded}  subtitle="API uploaded studies"      icon={<Upload className="h-4 w-4 text-muted-foreground" />} tooltip="Total API uploaded studies" />}
          {api.processed > 0 && <MetricCard title="API AI Processed"   value={api.processed} subtitle="Total API processed by AI" icon={<Cpu    className="h-4 w-4 text-muted-foreground" />} tooltip="Total API studies analysed by AI" />}
        </div>
        <p className="text-xs text-muted-foreground text-right">
          Last updated: {new Date(data.timestamp).toLocaleString()}
        </p>
      </div>
    </TooltipProvider>
  );
}

// ─── MetricsSkeleton ──────────────────────────────────────────────────────────

function MetricsSkeleton() {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {[...Array(2)].map((_, i) => (
        <Card key={i}>
          <CardHeader className="pb-2"><div className="h-4 w-32 bg-muted rounded animate-pulse" /></CardHeader>
          <CardContent><div className="h-8 w-16 bg-muted rounded animate-pulse" /></CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── MetricCard ───────────────────────────────────────────────────────────────

function MetricCard({ title, value, subtitle, icon, tooltip }: {
  title: string; value: number; subtitle: string; icon: React.ReactNode; tooltip: string;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Tooltip>
          <TooltipTrigger asChild>{icon}</TooltipTrigger>
          <TooltipContent side="right"><p>{tooltip}</p></TooltipContent>
        </Tooltip>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value.toLocaleString()}</div>
        <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
      </CardContent>
    </Card>
  );
}